// APP-1282 (s-auth0-token-renewal-hardening-019eb27d): single source of truth
// for the post-login destination rule, shared by ProtectedRoute.returnTo (which
// captures where to come *back* to before redirecting to Auth0) and
// onRedirectCallback (which navigates there after the OAuth round-trip).
//
// The rule: the application must never land on the OAuth callback route
// (`/auth/*`). By the time we'd navigate there its single-use `code` is already
// consumed, so re-rendering the callback re-enters the auth guard and loops.
// Any `/auth/*` target collapses to "/"; everything else is preserved verbatim
// (full pathname + query + hash, so APP-1279 deep links like
// /rebalances/today?q=… survive a cold load).
//
// The OAuth `redirect_uri` (/auth/auth0) is unaffected — this only governs the
// in-app destination, not the round-trip through the callback.

type Loc = Pick<Location, "pathname" | "search" | "hash">;

/**
 * Resolve the post-login destination.
 *
 * @param rawTarget A pending target string (e.g. `appState.returnTo`), if any.
 *   When provided it is evaluated as the candidate destination directly.
 * @param loc Falls back to the current location (pathname + search + hash) when
 *   `rawTarget` is undefined. Defaults to `window.location`.
 * @returns A safe in-app path: "/" for any `/auth/*` target (or empty/undefined),
 *   otherwise the candidate preserved verbatim.
 */
export function resolvePostAuthTarget(
  rawTarget: string | undefined,
  loc: Loc = window.location,
): string {
  const target = rawTarget ?? `${loc.pathname}${loc.search}${loc.hash}`;
  if (!target || target.startsWith("/auth/")) return "/";
  return target;
}
